import { Code, ConnectError } from '@connectrpc/connect'
import {
  WorkflowTemplateErrorReason,
  WorkflowTemplateErrorReasonSchema,
} from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'

import {
  type AppErrorInfoReasonEntry,
  readAppErrorInfo,
} from '~/libs/appErrorInfo'

/**
 * `WorkflowTemplateErrorReason` (backend `workflowtemplate` domain) → i18n key。
 * 雛形の削除確認モーダル内に出す operation-scoped のエラー。
 */
const DELETE_REASON_KEYS: Record<
  WorkflowTemplateErrorReason,
  AppErrorInfoReasonEntry | undefined
> = {
  [WorkflowTemplateErrorReason.UNSPECIFIED]: undefined,
  [WorkflowTemplateErrorReason.IN_USE_BY_AUTO_CREATION_RULE]: {
    key: 'page:adminWorkflowTemplates.serverError.reason.inUseByAutoCreationRule',
  },
  [WorkflowTemplateErrorReason.DEMAND_FORECAST_LINK_NOT_UNIQUE]: undefined,
}

/**
 * 雛形の削除の失敗をモーダル内の文言の i18n key にする。 reason があればその文言
 * (AC-WFT-061)、 無ければ楽観ロック衝突 (Aborted) は再読み込みを促す文言、
 * それ以外は削除失敗の文言 (AC-WFT-017c)。
 */
export function resolveWorkflowTemplateDeleteErrorKey(err: unknown): string {
  if (err instanceof ConnectError) {
    const info = readAppErrorInfo(err, WorkflowTemplateErrorReasonSchema)
    const entry =
      info === undefined ? undefined : DELETE_REASON_KEYS[info.reason]
    if (entry !== undefined) return entry.key
    if (err.code === Code.Aborted) {
      return 'page:adminWorkflowTemplates.serverError.aborted'
    }
  }
  return 'page:adminWorkflowTemplates.serverError.deleteFailed'
}

/**
 * `WorkflowTemplateErrorReason` → i18n key for the save (Create / Update) of
 * the template editor. Operation-scoped (上部の集約 alert, AC-WFT-015): the
 * server reaches this only when the client schema found the duplicate in at
 * most one field, so no field is named.
 */
const SAVE_REASON_KEYS: Record<
  WorkflowTemplateErrorReason,
  AppErrorInfoReasonEntry | undefined
> = {
  [WorkflowTemplateErrorReason.UNSPECIFIED]: undefined,
  [WorkflowTemplateErrorReason.IN_USE_BY_AUTO_CREATION_RULE]: undefined,
  [WorkflowTemplateErrorReason.DEMAND_FORECAST_LINK_NOT_UNIQUE]: {
    key: 'page:adminWorkflowTemplates.validation.demandForecastAtMostOne',
  },
}

/**
 * 雛形の保存の失敗に reason があれば、その文言の i18n key (AC-WFT-040)。
 * reason が無い・未登録なら undefined (caller の code ベースの fallback に任せる)。
 */
export function resolveWorkflowTemplateSaveErrorKey(
  err: unknown,
): string | undefined {
  if (!(err instanceof ConnectError)) return undefined
  const info = readAppErrorInfo(err, WorkflowTemplateErrorReasonSchema)
  return info === undefined ? undefined : SAVE_REASON_KEYS[info.reason]?.key
}
